import React, { useContext } from 'react';
import { ThemeContext } from 'styled-components';
import { useHistory, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  VStack,
  Box,
  Image,
  Button,
  Text,
  HStack,
} from '@wegroup/design-system';

// Components
import WeGroupLogoWhite from '../../../../common/src/assets/WeGroupLogoWhite';
import ToolbarProgressBar from './ToolbarProgressBar';

// Redux
import { isStaging } from '../../../../common/src/utils/globalVariables';
import { stringify } from 'query-string';
import ToolbarBackToDashboardButton from './ToolbarBackToDashboardButton';
import { useLouiseSelector } from '../../store/redux/store';
import useSessionId from '../../../../flows/src/hooks/useSessionId';
import useAuth from '../../../../app/src/features/authentication/hooks/useAuth';
import LanguageChangeMenu from '../../../../flows/src/components/ui/advisory-flow/components/LanguageChangeMenu';
import { isSocialLoginUrl } from '../../../../app/src/features/authentication/constants/SocialAuth';

export const TOOLBAR_MAX_WIDTH = {
  base: '80',
  laptopL: '96',
};

const Toolbar: React.FC<React.PropsWithChildren<unknown>> = () => {
  const location = useLocation();
  const { distribution } = useAuth();
  const campaign = useLouiseSelector((state) => state.productFactory.campaign);
  // Need theme from themeProvider to get 'isImmo' prop
  const theme = useContext(ThemeContext);
  const history = useHistory();
  const { t } = useTranslation();

  const sessionId = useSessionId();
  const isOfferPage = !!location.pathname.match(
    new RegExp('/product/session/(offers).*') || false,
  );

  const openDevTools = () => {
    if (sessionId && isStaging) {
      const queryString = stringify({ lastRoute: location.pathname });
      history.push(
        `/product/session/devtools/${sessionId}/overview?${queryString}`,
      );
    }
  };

  return (
    <VStack
      data-test-id="ProdFac_Toolbar"
      pt="8"
      pr={isOfferPage ? '0' : '10'}
      pb="6"
      pl="6"
      zIndex="3"
      alignItems="stretch"
      justifyContent="space-between"
      bgColor={theme['isImmo'] ? theme.secondary[700] : theme.secondary[800]}
      minW="8"
      borderTopRightRadius="20px"
      borderBottomRightRadius="20px"
      color="white"
      w="100%"
      maxW={TOOLBAR_MAX_WIDTH}
      spacing={{ base: '2', laptopL: '8' }}
    >
      <div>
        {(!isSocialLoginUrl || !theme.branding?.logo) &&
        distribution?.id === '36d87970-b81d-4425-bb7f-f23270f13637' ? (
          <WeGroupLogoWhite />
        ) : (
          <Box
            display={{ base: 'none', tabletS: 'flex' }}
            mr="4"
            minW="24"
            maxW="72"
            minH="12"
            maxH="24"
            w="100%"
            h="100%"
          >
            <Image
              objectFit="contain"
              w="100%"
              h="100%"
              src={
                campaign?.logo ||
                distribution?.logos?.logo_fe ||
                distribution?.logos?.logo_fe ||
                distribution?.logos?.logo ||
                theme.branding?.logo
              }
              alt={distribution?.name}
            />
          </Box>
        )}
        {!isSocialLoginUrl && <ToolbarBackToDashboardButton />}
      </div>
      <ToolbarProgressBar />
      <VStack align="stretch" spacing={{ base: '2', laptopL: '8' }}>
        <LanguageChangeMenu />
        <HStack spacing="4">
          <VStack align="left" spacing="2">
            {distribution.privacy_page_url && (
              <Button
                variant="link"
                alignSelf="flex-start"
                href={distribution.privacy_page_url}
                color="secondary.300"
                fontSize="small"
                target="_blank"
                as="a"
              >
                {t('Privacy')}
              </Button>
            )}

            <Text color="secondary.50" fontSize="small" onClick={openDevTools}>
              &copy; {new Date().getFullYear()} WeGroup NV
            </Text>
          </VStack>
        </HStack>
      </VStack>
    </VStack>
  );
};

export default Toolbar;
